<template>
  <EvaluacionesTabContent :fetchStatus="fetchEvaluationStatus" :onRetry="fetchEvaluacionesDesempenio">
    <template #success>
      <v-container>
        <v-row justify="end">
          <v-col cols="auto">
            <v-btn icon title="Actualizar listado" @click="fetchEvaluacionesDesempenio()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel v-for="(grupoEvaluaciones, index) in programacionEvaluaciones" :key="index">
                <v-expansion-panel-header>
                  <div>
                    <v-icon class="mr-2 secondary--text">mdi-folder</v-icon>
                    {{ grupoEvaluaciones.header }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list color="transparent">
                    <v-list-item
                      v-for="(evaluacion, evaluacionKey) in grupoEvaluaciones.evaluaciones"
                      :key="evaluacionKey"
                    >
                      <v-list-item-content>
                        <v-list-item-title> {{ EVALUATION_TYPES[evaluacionKey] }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <EvaluacionStatusChip :status="evaluacion.CatEstatusEvaluacion.title" />
                      </v-list-item-action>
                      <v-list-item-action>
                        <v-btn icon @click="openEvaluacionDialog(evaluacion, evaluacionKey)">
                          <v-icon color="secondary">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <v-btn small @click="exportExcel(grupoEvaluaciones.evaluaciones)" color="secondary">
                    Descargar cadena de resultados
                  </v-btn>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </EvaluacionesTabContent>
</template>

<script>
import EvaluacionesTabContent from '../../common/tabs/EvaluacionesTabContent.vue'
import EvaluacionStatusChip from '../../evaluacion-desempeño/chips/EvaluacionStatusChip.vue'
import { ACTION_STATUS, API_DATE_FORMAT, VUETIFY_DATE_FORMAT } from '@/const'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import DateAdapter from '@/utils/DateAdapter'
import findSiblingEvaluation from '@/modules/evaluacion-desempeño/domain/rules/findSiblingEvaluation'
import { DIALOG_TYPES } from '@/plugins/dialog-types'
import DesempeñoInstitucional from '@/modules/desempeño-institucional/domain/DesempeñoInstitucional'
import RendimientoProfesionalismo from '@/modules/rendimiento-profesionalismo/domain/RendimientoProfesionalismo'
import RespetoValoresPrincipios from '@/modules/respeto-valores-principios/domain/RespetoValoresPrincipios'
import CadenaResultadosDesempenioAcademico from '@/modules/evaluacion-desempeño-academico/domain/entities/CadenaResultadosDesempenioAcademico'

const EVALUATION_TYPES = {
  desempeñoInstitucional: 'Desempeño Institucional',
  rendimientoProfesionalismo: 'Rendimiento y Profesionalismo',
  respetoPrincipiosValores: 'Respeto a Principios y Valores',
}

export default {
  components: {
    EvaluacionesTabContent,
    EvaluacionStatusChip,
  },

  mounted() {
    this.fetchEvaluacionesDesempenio()
  },

  data() {
    return {
      EVALUATION_TYPES,
      ACTION_STATUS,
      fetchEvaluationStatus: ACTION_STATUS.IDLE,
      selectedProgramacionEvaluacion: null,
    }
  },

  computed: {
    ...mapGetters('personal', { personal: 'getPersonalSingle' }),
    ...mapGetters('desempeñoInstitucional', { desempeñoInstitucional: 'getDesempeñoInstitucional' }),
    ...mapGetters('rendimientoProfesionalismo', { rendimientoProfesionalismo: 'getRendimientoProfesionalismo' }),
    ...mapGetters('respetoPrincipiosValores', { respetoPrincipiosValores: 'getRespetoPrincipiosValores' }),

    programacionEvaluaciones() {
      const groupedEvaluaciones = []

      // Use desempeñoInstitucional as the main evaluation list to group by date
      const mainEvaluaciones = [...this.desempeñoInstitucional]

      mainEvaluaciones.forEach((evaluacion) => {
        const rendimientoProfesionalismo = findSiblingEvaluation(
          evaluacion.fechaAplicacion,
          this.rendimientoProfesionalismo
        )
        const respetoPrincipiosValores = findSiblingEvaluation(
          evaluacion.fechaAplicacion,
          this.respetoPrincipiosValores
        )

        groupedEvaluaciones.push({
          header: new DateAdapter(evaluacion.fechaAplicacion, API_DATE_FORMAT).toHumanReadable(),
          evaluaciones: {
            desempeñoInstitucional: { ...evaluacion },
            ...(rendimientoProfesionalismo ? { rendimientoProfesionalismo } : {}),
            ...(respetoPrincipiosValores ? { respetoPrincipiosValores } : {}),
          },
        })
      })

      return groupedEvaluaciones
    },
  },

  methods: {
    ...mapActions('desempeñoInstitucional', ['fetchDesempeñoInstitucional']),
    ...mapActions('rendimientoProfesionalismo', ['fetchRendimientoProfesionalismo']),
    ...mapActions('respetoPrincipiosValores', ['fetchRespetoPrincipiosValores']),
    ...mapMutations('dialog', ['SET_DIALOG']),
    ...mapMutations('evaluacionesPersonal', ['SET_SELECTED_EVALUATION']),
    ...mapActions('reactivoValor', ['fetchReactivoValor']),
    ...mapActions('reactivosEvaluacion', [
      'fetchReactivosDesempenioInstitucional',
      'fetchReactivosRendimientoProfesionalismo',
    ]),

    async openEvaluacionDialog(evaluacion, evaluacionKey) {
      let evaluacionEntity
      if (evaluacionKey === 'desempeñoInstitucional') {
        await this.fetchReactivosDesempenioInstitucional()
        evaluacionEntity = DesempeñoInstitucional.fromDto(evaluacion)
      }

      if (evaluacionKey === 'rendimientoProfesionalismo') {
        await this.fetchReactivosRendimientoProfesionalismo()
        evaluacionEntity = RendimientoProfesionalismo.fromDto(evaluacion)
      }

      if (evaluacionKey === 'respetoPrincipiosValores') {
        await this.fetchReactivoValor()
        evaluacionEntity = RespetoValoresPrincipios.fromDto(evaluacion)
      }

      this.SET_SELECTED_EVALUATION(evaluacionEntity)
      this.SET_DIALOG(DIALOG_TYPES.EDITAR_EVALUACION_DESEMPEÑO_ACADEMICO)
    },

    async fetchEvaluacionesDesempenio() {
      try {
        this.fetchEvaluationStatus = ACTION_STATUS.LOADING

        await Promise.all([
          this.fetchRendimientoProfesionalismo(this.personal.idPersonal),
          this.fetchRespetoPrincipiosValores(this.personal.idPersonal),
          this.fetchDesempeñoInstitucional(this.personal.idPersonal),
        ])

        this.fetchEvaluationStatus = ACTION_STATUS.SUCCEEDED
      } catch (error) {
        this.fetchEvaluationStatus = ACTION_STATUS.FAILED
        console.error('Error fetching evaluations:', error)
      }
    },

    exportExcel(grupoEvaluaciones) {
      new CadenaResultadosDesempenioAcademico(grupoEvaluaciones, this.personal).generateXlsx()
    },
  },
}
</script>
